import React from 'react';

import styles from './SectorAllocation.css';

import Card from '../../components/Card/Card';
import ErrorMessageNewFund from '../../components/ErrorMessages/ErrorMessageNewFund';
import ErrorMessageNoData from '../../components/ErrorMessages/ErrorMessageNoData';
import GqlQueryWrapper from '../../components/GqlQueryWrapper/GqlQueryWrapper';
import PercentageChart from '../../components/PercentageChart/PercentageChart';
import Legend from '../../components/Legend/Legend';
import TablePreRender from '../../components/Table/Table.prerender';
import TitleWithDescription from '../../components/TitleWithDescription/TitleWithDescription';
import { sectorAllocationUtil, sectorAllocationUtilETF } from './util';
import { MF_SECTOR_ALLOCATION, ETF_SECTOR_ALLOCATION } from './queries';
import {
    showSectorCustomReportingDisclaimer,
    isFixedIncomeETF
} from '../../utils/fund-info';
import { CARD_NAMES } from '../../utils/constants';
import { isExpectingMonthlyData } from '../../utils/time';

const customStructureTickers = [
    'PRHSX',
    'PRISX',
    'PRMTX',
    'PRNEX',
    'PRSCX',
    'TRREX'
];

const GICSDisclaimerTickers = [
    'GTFBX',
    'MDXBX',
    'NJTFX',
    'PBDIX',
    'PGMSX',
    'PRCIX',
    'PRCPX',
    'PRELX',
    'PREMX',
    'PRFHX',
    'PRFRX',
    'PRFSX',
    'PRGMX',
    'PRHYX',
    'PRIHX',
    'PRINX',
    'PRIPX',
    'PRMDX',
    'PRNYX',
    'PRPIX',
    'PRSMX',
    'PRSNX',
    'PRTAX',
    'PRTIX',
    'PRULX',
    'PRVAX',
    'PRWBX',
    'PRXCX',
    'PRXIX',
    'PTTFX',
    'RPEIX',
    'RPIBX',
    'RPIEX',
    'RPIHX',
    'RPISX',
    'RPOIX',
    'RPSIX',
    'TECIX',
    'TEIMX',
    'TNBMX',
    'TNIBX',
    'TRBFX',
    'TRBUX',
    'TRECX',
    'TUHYX'
];

const balancedFundTickers = ['RPBAX'];

const SectorAllocationCard = ({ etf, ticker, moneyMarket }) => (
    <Card>
        <GqlQueryWrapper
            query={etf ? ETF_SECTOR_ALLOCATION : MF_SECTOR_ALLOCATION}
            variables={{ ticker }}
            dataFunc={etf ? sectorAllocationUtilETF : sectorAllocationUtil}
            placeholder={() => <TablePreRender />}
        >
            {({
                data: {
                    asOfDate,
                    correspondingFundTicker,
                    data,
                    inceptionDate,
                    hasValues = true,
                    chartData
                }
            }) => {
                if (!chartData.length || !hasValues) {
                    if (!isExpectingMonthlyData(inceptionDate)) {
                        return (
                            <ErrorMessageNewFund
                                cardName={CARD_NAMES.SECTOR_ALLOCATION}
                            />
                        );
                    }
                    return (
                        <ErrorMessageNoData
                            cardName={CARD_NAMES.SECTOR_ALLOCATION}
                        />
                    );
                }

                const disclaimer = (() => {
                    // @TODO these uppdates are becoming unweildy very fast
                    // we need a content system for managing these legalese
                    // @Rally @REF: US143472
                    if (showSectorCustomReportingDisclaimer(ticker)) {
                        return `T. Rowe Price uses a custom structure for sector
                                and industry reporting for this product.
                                The custom structure changed on August 31, 2019,
                                and historical representations have been restated.`;
                    }
                    if (
                        customStructureTickers.includes(ticker) ||
                        customStructureTickers.includes(correspondingFundTicker)
                    ) {
                        return `T. Rowe Price uses a custom structure for sector
                                and industry reporting for this product.`;
                    }

                    return (
                        (moneyMarket && (
                            <div className="disclaimer">
                                Other assets less liabilities may include cash
                                and receivables and payables related to open
                                security or capital stock trades.
                            </div>
                        )) ||
                        (!(
                            GICSDisclaimerTickers.includes(ticker) ||
                            GICSDisclaimerTickers.includes(
                                correspondingFundTicker
                            ) ||
                            isFixedIncomeETF(ticker)
                        ) &&
                            `T. Rowe Price uses the current MSCI/S&P Global
                        Industry Classification Standard (GICS) for
                        sector and industry reporting. T. Rowe Price
                        will adhere to all updates to GICS for
                        prospective reporting.`)
                    );
                })();

                return (
                    <div className="container">
                        <TitleWithDescription
                            title={
                                moneyMarket
                                    ? 'Security Diversification'
                                    : `Sector Allocation${
                                          balancedFundTickers.includes(
                                              ticker
                                          ) ||
                                          balancedFundTickers.includes(
                                              correspondingFundTicker
                                          )
                                              ? ' - Equity Only'
                                              : ''
                                      }`
                            }
                        />
                        <div className="subheader">
                            <div className="left">
                                As a percentage of Total Net Assets
                            </div>
                            <div className="right">As of {asOfDate}</div>
                        </div>
                        <PercentageChart
                            data={chartData
                                // some of the percentTotalNetAssets is negative
                                // because of negative financial positions / debt obligations
                                // elide these from chart
                                .filter(({ value }) => value > 0)
                                .map((d) => [d.label, d.value, d.color])}
                        />
                        <div className="legend-container">
                            <Legend data={data} vertical />
                        </div>
                        <p>
                            Numbers may not add up to 100% exactly due to
                            rounding and/or the exclusion of reserves and other
                            assets.
                        </p>
                        {disclaimer}
                    </div>
                );
            }}
        </GqlQueryWrapper>

        <style jsx>{styles}</style>
    </Card>
);

export default SectorAllocationCard;
